// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, finalize, mergeMap } from "rxjs/operators"
import * as Actions from "../actions"
import { DeleteTimeGroupRequest, } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import * as TimelineSlice from "../slices/timeline"
import { Empty } from "google-protobuf/google/protobuf/empty_pb"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type DeleteTimeGroupRpc = (request: DeleteTimeGroupRequest) => Observable<Empty>

export const deleteTimeGroups = (deleteTimeGroupRpc: DeleteTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTimeGroups.match),
      mergeMap(({ payload: { request, selectedTimeRowsIds, resetProgress }}) =>
        deleteTimeGroupRpc(request)
          .pipe(
            mergeMap(() => concat(
              of(TimelineSlice.deleteTimeGroups(request.getTimeGroupIdsList())),
              of(TimelineSlice.addActiveTimeRowIds({ timeRowIds: selectedTimeRowsIds })),
            )),
            catchError(handleError),
            finalize(resetProgress),
          )
      ),
    )

const deleteTimeGroupRpc = (request: DeleteTimeGroupRequest): Observable<Empty> =>
  grpc(() => timelineClient.deleteTimeGroup(request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_delete_time_groups_failed_precondition",
            message: "Failed to delete timesheet(s). May be out of sync."
          }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_delete_time_groups_failed",
      message: "Failed to delete timesheet(s). Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
}

export default deleteTimeGroups(deleteTimeGroupRpc)
